/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jun 20 2023
 **/

import { timer } from "rxjs";
import styled from "@emotion/styled";

import { Column, Row, Typography } from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _P, _T } from "@rfice/language";
import { ChatRenderer } from "@/frameworks/core/components";
import { useFileExtensions } from "@/frameworks/core/utils";
import { useProperties } from "@/frameworks/libraries";

import { S } from "./private";

function View({ reply }: { id: string; reply: WebServices.Chat.Messages.Data["content"][number]["replyMessage"] }) {
	const { getFileExtension } = useFileExtensions();
	const properties = useProperties();

	const scrollToTarget = (ev: React.MouseEvent) => {
		ev.preventDefault();
		const target = ev.currentTarget as HTMLElement;
		const targetID = target.dataset.id?.substring(1);
		if (targetID) {
			const targetElement = document.getElementById(targetID);
			if (targetElement) {
				targetElement.scrollIntoView({ behavior: "smooth" });
				targetElement.classList.add("flash-animation");

				timer(2000).subscribe(() => {
					targetElement.classList.remove("flash-animation");
				});
			}
		}
	};

	const checkCrash = {
		elements: [
			{
				text: _T("삭제된 메시지입니다."),
				type: "text"
			}
		],
		type: "richtext"
	};

	const parsedFileData = reply?.files?.map((item) => {
		return JSON.parse(item.data);
	});
	const files = parsedFileData?.filter((file) => file.type === "file");

	const images = parsedFileData?.filter((file) => file.type === "image");
	const videos = files?.filter((file) => {
		return getFileExtension(file.mime === "video/quicktime" ? "video/mp4" : file.mime) === "video";
	});

	const hasVideos = videos && videos.length > 0;
	const hasImages = images && images?.length > 0;

	const mediaLabel = (() => {
		const imageCount = images?.length ?? 0;
		const videoCount = videos?.length ?? 0;

		if (imageCount > 0 && videoCount > 0) {
			return _T("동영상 {{videoCount}}개, 사진 {{imageCount}}개", {
				videoCount,
				imageCount
			});
		}

		if (videoCount > 0) {
			return videoCount === 1 ? _T("동영상") : _T("동영상 {{count}}개", { count: videoCount });
		}
		if (imageCount > 0) {
			return imageCount === 1 ? _T("사진") : _T("사진 {{count}}개", { count: imageCount });
		}
	})();

	if (!reply) return;
	return (
		<Column
			data-id={`#${reply?.roomMessageId}`}
			css={{
				position: "relative",
				padding: "8px"
			}}
			onClick={scrollToTarget}
		>
			<Row gap={6}>
				{hasVideos ? (
					<ReplayVideo imageUrl={`${properties.default.webDomain}${videos?.[0]?.videoThumbnailPath}?h=360`} />
				) : hasImages ? (
					<ReplayImage imageUrl={`${images?.[0]?.url}?h=360`} />
				) : null}
				<Column gap={4}>
					<S.ReplyTo>
						{Configure.userID === reply?.senderId
							? _T("나에게 답장")
							: _P("<span>{{writer}} 에게 답장</span>", {
									writer: `<span id='target-view'>${reply?.senderName}</span>`
								})}
					</S.ReplyTo>
					<Typography typoSize="Body3" typoColor="neutral_50_100">
						{mediaLabel}
					</Typography>
				</Column>
			</Row>
			<S.Message>
				{reply.deleted ? (
					<Typography typoSize="Body2" typoColor="neutral_80_50">
						{_T("삭제된 메시지입니다.")}
					</Typography>
				) : (
					<ChatRenderer chatForm={reply?.content ? JSON.parse(reply?.content) : checkCrash} />
				)}
			</S.Message>
		</Column>
	);
}

function Marker() {
	return <div></div>;
}

const Reply = {
	View,
	Marker
};

const ReplayImage = styled.div<{ imageUrl?: string }>`
	width: 48px;
	height: 48px;
	border-radius: 4px;
	background-image: ${({ imageUrl }) => `url(${imageUrl})`};
	background-size: contain;
	background-position: center;
	background-repeat: no-repeat;
	background-color: ${({ theme }) => theme.colors.secondary_80_12};
`;

const ReplayVideo = styled(ReplayImage)`
	position: relative;

	&::after {
		content: "";
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 0;
		height: 0;
		border-left: 10px solid white;
		border-top: 6px solid transparent;
		border-bottom: 6px solid transparent;
		opacity: 0.9;
		pointer-events: none;
	}
`;
export default Reply;
